import { memo, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Breadcrumb from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { SubmitState } from "stores/lib";
import {
  useNotificationStore,
  NotificationConfig,
} from "stores/NotificationStore";

import NotificationConfigItem from "components/NotificationConfig";

// -----------------------------------------------------------------------------

const TinesWebhookNotificationConfigPage = memo(
  function TinesWebhookNotificationConfigPage() {
    const [notificationStore, notificationActions] = useNotificationStore();
    const { uuid } = useParams();

    const [findingEnable, setFindingEnable] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      const config = structuredClone(notificationStore.config);
      if (!config || !uuid) {
        return;
      }

      let enabled = false;

      switch (notificationStore.fetchingState) {
        case SubmitState.STARTED:
        case SubmitState.STARTED_AGAIN:
        case SubmitState.SUCCESS:
          config.finding_notification.outputs.tines_webhook.destinations.forEach(
            (destination) => {
              if (destination.connection === uuid) {
                enabled = true;
              }
            }
          );

          setFindingEnable(enabled);
          break;

        case SubmitState.ERROR:
          console.error("error");
      }
    }, [notificationStore.fetchingState, notificationStore.config, uuid]);

    // Fetch a fresh config to ensure no stale data
    useEffect(() => {
      notificationActions.fetchConfig();
    }, [notificationActions]);

    const onSuccess = () => {
      setLoading(false);
      toast.success("Successfully saved the notification settings", {
        position: "bottom-center",
      });
      notificationActions.fetchConfig();
    };

    const onError = () => {
      setLoading(false);
      toast.error("Something went wrong saving the notification settings", {
        position: "bottom-center",
      });
    };

    const saveConfig = () => {
      setLoading(true);
      const error = false;

      if (!uuid) return;

      const newConfig: Partial<NotificationConfig> = {};

      if (findingEnable) {
        newConfig.finding_notification = {
          outputs: {
            tines_webhook: {
              enabled: true,
              destinations: [
                {
                  filter: null,
                  connection: uuid,
                  specific_location: null,
                },
              ],
            },
          },
        };
      }

      if (error) {
        setLoading(false);
        return;
      }

      const config = newConfig.finding_notification ? newConfig : null;
      notificationActions.updateConfig(config, uuid, onSuccess, onError);
    };

    return (
      <>
        <Breadcrumbs>
          <Breadcrumb
            underline="none"
            color="inherit"
            component={Link}
            to="/environment/destinations"
          >
            Environment
          </Breadcrumb>
          <Breadcrumb
            underline="none"
            color="inherit"
            component={Link}
            to="/environment/destinations"
          >
            Destinations
          </Breadcrumb>
          <span />
        </Breadcrumbs>

        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
            Tines Webhook Connection
          </Typography>
          <Typography variant="body1">
            Configuring your Tines webhooks couldn&apos;t be easier. Simply
            enter the URL of your Tines endpoint and we&apos;ll take care of the
            rest. We&apos;ll send all of the data you need to keep your
            application in sync with our platform.
          </Typography>
        </Box>
        <Divider sx={{ mt: 2, mb: 2 }} />

        <Stack spacing={2}>
          <NotificationConfigItem
            enabled={findingEnable}
            label={"Finding Notifications"}
            description={
              "Notifications for security findings can be configured in a variety of ways, including the ability to receive a real-time notification whenever a new security finding is generated. We highly recommend that you configure your notifications to include security findings. This way, you will be alerted as soon as possible if there are any potential threats to your company that ThreatKey has detected."
            }
            setEnabled={setFindingEnable}
          />

          <Stack sx={{ mt: 2 }} spacing={2} direction="row">
            <LoadingButton
              variant="contained"
              onClick={saveConfig}
              loading={loading}
            >
              Save
            </LoadingButton>
            <Link
              to="/environment/destinations"
              style={{ textDecoration: "none" }}
            >
              <Button variant="text">Cancel</Button>
            </Link>
          </Stack>
        </Stack>
      </>
    );
  }
);

export default TinesWebhookNotificationConfigPage;
